import { SurveyInfo } from 'modules/application/applicant-survey/services/applicant-survey.service.class';
import { ApplicantWorkHistoryInfo } from 'modules/application/employment-history/services/employment-history.service.class';
import { ReferenceInfo } from 'modules/application/references/services/references.service.class';

// #region Applicant Address Info

export interface ApplicantAddressInfo {
    AddressType: string;
    AddressID: number;
    Street1: string;
    Street2: string;
    City: string;
    Region: string;
    PostalCode: string;
    Country: string;
    SameAddresses: boolean;
}

// #endregion

// #region Applicant Info

export interface ApplicantInfo {
    UserID: number;
    LastName: string;
    FirstName: string;
    MiddleName: string;
    NameSuffix: string;
    SSN: string;
    RequirePCPSConnect: boolean;
    DateOfBirth: Date | null;
    Gender: string;
    Ethnicity: string;
    RaceAsian: boolean;
    RaceAfricanAmerican: boolean;
    RaceIndianAlaskan: boolean;
    RacePacificIslander: boolean;
    RaceWhite: boolean;
    LegalWorker: boolean;
    DateUpdated: Date | null;
    DateSubmitted: Date | null;
    HasApplication: boolean;
    HasUnsubmittedChanges: boolean;
    AppProgress: number;
    AppAvailable: number | null;
    AppRequired: number | null;
    MailingAddressID: number | null;
    ResidenceAddressID: number | null;
    PrimaryPhone: string;
    PrimaryPhoneType: string;
    SecondaryPhone: string;
    SecondaryPhoneType: string;
    DriversLicState: string;
    DriversLicNumber: string;
    IsTeacherTransfer: boolean;
    Active: boolean;
    Email: string;
    TestURL: string | null;
    InterviewExists: boolean;
    ReferredBySAPID: string | null;
    ReferredByName: string | null;
    ExchangeTeacher: number | null;
    PassportNumber: string | null;
    PassportCountry: string | null;
    ExchangeTeacherCompany: string | null;
    HasMissingWorkHistoryEmails: boolean;
    HasMissingReferenceEmails: boolean;
    EmployeeStatus: string | null;
    CanChangeName: boolean | null;

    ApplicationTypes: ApplicantTypeInfo[];
    References: ReferenceInfo[];
    Survey: SurveyInfo | null;
    WorkHistory: ApplicantWorkHistoryInfo | null;
}

// #endregion

// #region Applicant Packet

export interface ApplicantPacket {
    Active: boolean | null;
    Application: ApplicantInfo | null;
    Email: string | null;
    HasApplication: boolean;
    PersonnelGroup: string | null;
    SAPID: string | null;
    UserID: number | null;
}

// #endregion

// #region Applicant Phone Info

export interface ApplicantPhoneInfo {
    UserID: number | null;
    PrimaryPhone: string;
    PrimaryPhoneType: string;
    SecondaryPhone: string;
    SecondaryPhoneType: string;
}

// #endregion

// #region Application Save Parameters

export interface ApplicationSaveParameters {
    IsUpdate: boolean;
    LastName: string | null;
    FirstName: string | null;
    MiddleName: string | null;
    NameSuffix: string | null;
    SSN: string | null;
    DateOfBirth: Date | null;
    Gender: string | null;
    Ethnicity: string | null;
    RaceAsian: boolean | null;
    RaceAfricanAmerican: boolean | null;
    RaceIndianAlaskan: boolean | null;
    RacePacificIslander: boolean | null;
    RaceWhite: boolean | null;
    LegalWorker: boolean | null;
    DriversLicState: string | null;
    DriversLicNumber: string | null;
    ExchangeTeacher: number | null;
    PassportNumber: string | null;
    PassportCountry: string | null;
    ExchangeTeacherCompany: string | null;
}

// #endregion

// #region Applicant Type Info

export interface ApplicantTypeInfo {
    ApplicantTypeID: number;
    ApplicationTypeID: number;
    TypeCode: string | null;
    ReferredByName?: string | null;
    ReferredBySAPID?: string | null;
}

// #endregion

// #region Applicant Type Update Result

export interface ApplicantTypeUpdateResult {
    Status: ApplicationStatus | null;
    ApplicationTypes: ApplicantTypeInfo[];
    IsTeacherTransfer: boolean;
    ReferredByFirstName: string | null;
    ReferredByLastName: string | null;
    ReferredBySAPID: string | null;
}

// #endregion

// #region Application Step

export enum ApplicationStep {
    ApplicationStatus = 0, // Not "officially" a step
    PersonalInfo = 1,
    ContactInfo = 2,
    ApplicationType = 4,
    FloridaCertification = 8,
    InstructionalQualifications = 16,
    EmploymentHistory = 32,
    EducationHistory = 64,
    References = 128,
    CriminalHistory = 256,
    WorkPerformance = 512,
    ParaSurvey = 1024,
    StudentTeaching = 2048,
    ProfessionalSanctions = 4096,
    SubjectAreaExams = 8192,
    CustodialSurvey = 16384,
    DriversLicense = 32768,
    StatementOfEligibility = 65536,
    ApplicantSurvey = 131072,
    ProfessionalQualifications = 262144,
    SubjectAreaInterest = 524288,
    TeacherTransfer = 1048576,
    OutOfStateCertification = 2097152,
    LegalNameDocumentation = 4194304,
    TranscriptsUploads = 8388608,
    ResumesCoverLetters = 16777216,
}

// #endregion

// #region Application Status

export interface ApplicationStatus {
    AppProgress: number | null;
    AppAvailable: number | null;
    AppRequired: number | null;
    HasUnsubmittedChanges: boolean | null;
    HasApplication: boolean;
    DateSubmitted: Date | null;
    Active: boolean;
    IsTeacherTransfer: boolean;
    TransferWithoutCanContact: boolean;
    ContactMostRecentNoEmail: boolean;
    SurveyPCSB26WeeksNonNull: boolean;
    ExchangeTeacher: number | null;
}

// #endregion

// #region Application Upsert Result

export interface ApplicationUpsertResult {
    Status: ApplicationStatus | null;
    AppPacket: ApplicantPacket;
}

// #endregion

// #region Non Teacher Transfer Steps

export const NonTeacherTransferSteps = [
    ApplicationStep.EducationHistory,
    ApplicationStep.WorkPerformance,
    ApplicationStep.References,
    ApplicationStep.CriminalHistory,
    ApplicationStep.ApplicantSurvey,
    ApplicationStep.ProfessionalQualifications,
    ApplicationStep.InstructionalQualifications,
    ApplicationStep.ProfessionalSanctions,
    ApplicationStep.SubjectAreaInterest,
    ApplicationStep.StudentTeaching,
    ApplicationStep.CustodialSurvey,
    ApplicationStep.ParaSurvey,
];

// #endregion

// #region Teacher Transfer Steps

export const TeacherTransferSteps = [
    ApplicationStep.ContactInfo,
    ApplicationStep.ApplicationType,
    ApplicationStep.EmploymentHistory,
    ApplicationStep.FloridaCertification,
    ApplicationStep.SubjectAreaExams,
    ApplicationStep.TeacherTransfer,
];

// #endregion
